import { Box, Flex, Text } from '@chakra-ui/react';
import { useFormikContext } from 'formik';
import { useEffect } from 'react';
import { BsCalculatorFill } from 'react-icons/bs';

import { FormikInput, FormikSelect } from 'components/Formik';
import { accrualMethods } from 'containers/CompanySettings/TimeOffSetup/shared/options';
import { weekDays, daysOfMonth, monthOptions } from 'global/options';

import { initialCategoryFormData } from '../../schema';

const CronExpressionGenerator = () => {
  const { values, setFieldValue } = useFormikContext<typeof initialCategoryFormData>();

  useEffect(() => {
    generateCronExpression('accrue_per_period', null, values.accrue_per_period);

    if (values.in_hours === 'false' && values.accrue_type?.value === 'Hourly') {
      setFieldValue(
        'accrue_type',
        accrualMethods.find(method => method.value === 'Daily')
      );
    }
  }, []);

  const getFieldValue = (
    typeName: keyof typeof initialCategoryFormData,
    fieldName: keyof typeof initialCategoryFormData,
    fieldValue: ILabelValueOptions | null
  ) => {
    return typeName === fieldName ? (fieldValue?.value ?? '*') : (values[fieldName]?.value ?? '*');
  };

  const generateCronExpression = (
    typeName: keyof typeof initialCategoryFormData,
    fieldValue: ILabelValueOptions | null,
    accruePerPeriod: string = values.accrue_per_period
  ) => {
    const accrueType = getFieldValue(typeName, 'accrue_type', fieldValue);
    const accrueWeekDay = getFieldValue(typeName, 'accrue_week_day', fieldValue);
    const accrueMonthDayOne = getFieldValue(typeName, 'accrue_month_day_one', fieldValue);
    const accrueMonthDayTwo = getFieldValue(typeName, 'accrue_month_day_two', fieldValue);
    const accrueYearDay = getFieldValue(typeName, 'accrue_year_day', fieldValue);
    const accrueYearMonthOne = getFieldValue(typeName, 'accrue_year_month_one', fieldValue);
    const accrueYearMonthTwo = getFieldValue(typeName, 'accrue_year_month_two', fieldValue);
    const accrueYearMonthThree = getFieldValue(typeName, 'accrue_year_month_three', fieldValue);
    const accrueYearMonthFour = getFieldValue(typeName, 'accrue_year_month_four', fieldValue);

    let accrue_period = '59 23 * * *';
    let totalAccrualDays = 0;

    switch (accrueType) {
      case 'Hourly':
        accrue_period = '59 23 * * *';
        totalAccrualDays = 365 * Number(accruePerPeriod);
        break;

      case 'Daily':
        accrue_period = '59 23 * * *';
        totalAccrualDays = 365 * Number(accruePerPeriod);
        break;

      case 'Weekly':
        accrue_period = `59 23 * * ${accrueWeekDay}`;
        totalAccrualDays = 52 * Number(accruePerPeriod);
        break;

      case 'Bi-Monthly':
        accrue_period = `59 23 ${accrueMonthDayOne},${accrueMonthDayTwo} * *`;
        totalAccrualDays = 24 * Number(accruePerPeriod);
        break;

      case 'Monthly':
        accrue_period = `59 23 ${accrueMonthDayOne} * *`;
        totalAccrualDays = 12 * Number(accruePerPeriod);
        break;

      case 'Quarterly':
        accrue_period = `59 23 ${accrueYearDay} ${accrueYearMonthOne},${accrueYearMonthTwo},${accrueYearMonthThree},${accrueYearMonthFour} *`;
        totalAccrualDays = 4 * Number(accruePerPeriod);
        break;

      case 'Semi-Annually':
        accrue_period = `59 23 ${accrueYearDay} ${accrueYearMonthOne},${accrueYearMonthTwo} *`;
        totalAccrualDays = 2 * Number(accruePerPeriod);
        break;

      case 'Annually':
        accrue_period = `59 23 ${accrueYearDay} ${accrueYearMonthOne} *`;
        totalAccrualDays = 1 * Number(accruePerPeriod);
        break;

      case 'Anniversary':
        accrue_period = '59 23 * * *';
        totalAccrualDays = 1 * Number(accruePerPeriod);
        break;

      default:
        accrue_period = '59 23 * * *';
    }

    setFieldValue('totalAccrualDays', totalAccrualDays);
    setFieldValue('accrue_period', accrue_period);
  };

  const handleAccruePerPeriodChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    generateCronExpression('accrue_per_period', null, e.target.value);
  };

  return (
    <div>
      <Flex alignItems="flex-start" gridGap="3" mb="2" flexWrap="wrap">
        <Text as="span" pt="4">
          Users accrue
        </Text>
        <FormikInput
          name="accrue_per_period"
          formControlProps={{ minWidth: '64px', width: '64px', mb: 0 }}
          onChangeCallback={handleAccruePerPeriodChange}
        />
        {values.accrue_type?.value !== 'Hourly' ? (
          <Text as="span" pt="4">
            {values.in_hours === 'false' ? 'days' : 'hours'}
          </Text>
        ) : null}
        <FormikSelect
          name="accrue_type"
          options={
            values.in_hours === 'false' ? accrualMethods.filter(method => method.value !== 'Hourly') : accrualMethods
          }
          selectProps={{ isClearable: false, placeholder: '' }}
          formControlProps={{ minWidth: '170px', width: '170px', mb: 0 }}
          onChangeCallback={fieldValue => generateCronExpression('accrue_type', fieldValue)}
        />
        {values.accrue_type?.value === 'Hourly' ? (
          <>
            <Text as="span" pt="4">
              for every
            </Text>
            <FormikInput
              name="accrue_hourly"
              formControlProps={{ minWidth: '70px', width: '70px', mb: 0 }}
              onChangeCallback={() => generateCronExpression('accrue_hourly', null)}
            />
            <Text as="span" pt="4">
              hours worked
            </Text>
          </>
        ) : null}
        {values.accrue_type?.value === 'Weekly' ? (
          <>
            <Text as="span" pt="4">
              on
            </Text>
            <FormikSelect
              name="accrue_week_day"
              options={weekDays}
              selectProps={{ isClearable: false, placeholder: '' }}
              formControlProps={{ minWidth: '180px', width: '180px', mb: 0 }}
              onChangeCallback={fieldValue => generateCronExpression('accrue_week_day', fieldValue)}
            />
          </>
        ) : null}
        {values.accrue_type?.value === 'Bi-Monthly' || values.accrue_type?.value === 'Monthly' ? (
          <>
            <Text as="span" pt="4">
              on the
            </Text>
            <FormikSelect
              name="accrue_month_day_one"
              options={daysOfMonth}
              selectProps={{ isClearable: false, placeholder: '' }}
              formControlProps={{ minWidth: '86px', width: '86px', mb: 0 }}
              onChangeCallback={fieldValue => generateCronExpression('accrue_month_day_one', fieldValue)}
            />
          </>
        ) : null}
        {values.accrue_type?.value === 'Bi-Monthly' ? (
          <>
            <Text as="span" pt="4">
              and
            </Text>
            <FormikSelect
              name="accrue_month_day_two"
              options={daysOfMonth}
              selectProps={{ isClearable: false, placeholder: '' }}
              formControlProps={{ minWidth: '86px', width: '86px', mb: 0 }}
              onChangeCallback={fieldValue => generateCronExpression('accrue_month_day_two', fieldValue)}
            />
          </>
        ) : null}
      </Flex>

      <Box>
        {values.accrue_type?.value === 'Quarterly' ||
        values.accrue_type?.value === 'Semi-Annually' ||
        values.accrue_type?.value === 'Annually' ? (
          <>
            {/* First Month */}
            <Flex gridGap="2" justifyContent="flex-end" mb="2">
              <Text as="span" pt="4">
                on the
              </Text>
              <FormikSelect
                name="accrue_year_day"
                options={daysOfMonth}
                selectProps={{ isClearable: false, placeholder: '' }}
                formControlProps={{ minWidth: '86px', width: '86px', mb: 0 }}
                onChangeCallback={fieldValue => generateCronExpression('accrue_year_day', fieldValue)}
              />
              <Text as="span" pt="4">
                day of
              </Text>
              <FormikSelect
                name="accrue_year_month_one"
                options={monthOptions}
                selectProps={{ isClearable: false, placeholder: '' }}
                formControlProps={{ minWidth: '130px', width: '130px', mb: 0 }}
                onChangeCallback={fieldValue => generateCronExpression('accrue_year_month_one', fieldValue)}
              />
              <Text as="span" pt="4">
                {values.accrue_type?.value !== 'Annually' ? 'and' : null}
              </Text>
            </Flex>
          </>
        ) : null}

        {values.accrue_type?.value === 'Quarterly' || values.accrue_type?.value === 'Semi-Annually' ? (
          // Second Month
          <Flex gridGap="2" justifyContent="flex-end" mb="2">
            <Text as="span" pt="4">
              on the
            </Text>
            <FormikSelect
              name="accrue_year_day"
              options={daysOfMonth}
              selectProps={{ isClearable: false, placeholder: '' }}
              formControlProps={{ minWidth: '86px', width: '86px', mb: 0 }}
              onChangeCallback={fieldValue => generateCronExpression('accrue_year_day', fieldValue)}
            />
            <Text as="span" pt="4">
              day of
            </Text>
            <FormikSelect
              name="accrue_year_month_two"
              options={monthOptions}
              selectProps={{ isClearable: false, placeholder: '' }}
              formControlProps={{ minWidth: '130px', width: '130px', mb: 0 }}
              onChangeCallback={fieldValue => generateCronExpression('accrue_year_month_two', fieldValue)}
            />
            <Box as="span" pt="4" visibility={values.accrue_type?.value === 'Quarterly' ? 'visible' : 'hidden'}>
              and
            </Box>
          </Flex>
        ) : null}

        {values.accrue_type?.value === 'Quarterly' ? (
          <>
            {/* Third Month */}
            <Flex gridGap="2" justifyContent="flex-end" mb="2">
              <Text as="span" pt="4">
                on the
              </Text>
              <FormikSelect
                name="accrue_year_day"
                options={daysOfMonth}
                selectProps={{ isClearable: false, placeholder: '' }}
                formControlProps={{ minWidth: '86px', width: '86px', mb: 0 }}
                onChangeCallback={fieldValue => generateCronExpression('accrue_year_day', fieldValue)}
              />
              <Text as="span" pt="4">
                day of
              </Text>
              <FormikSelect
                name="accrue_year_month_three"
                options={monthOptions}
                selectProps={{ isClearable: false, placeholder: '' }}
                formControlProps={{ minWidth: '130px', width: '130px', mb: 0 }}
                onChangeCallback={fieldValue => generateCronExpression('accrue_year_month_three', fieldValue)}
              />
              <Text as="span" pt="4">
                and
              </Text>
            </Flex>

            {/* Fourth Month */}
            <Flex gridGap="2" justifyContent="flex-end">
              <Text as="span" pt="4">
                on the
              </Text>
              <FormikSelect
                name="accrue_year_day"
                options={daysOfMonth}
                selectProps={{ isClearable: false, placeholder: '' }}
                formControlProps={{ minWidth: '86px', width: '86px', mb: 0 }}
                onChangeCallback={fieldValue => generateCronExpression('accrue_year_day', fieldValue)}
              />
              <Text as="span" pt="4">
                day of
              </Text>
              <FormikSelect
                name="accrue_year_month_four"
                options={monthOptions}
                selectProps={{ isClearable: false, placeholder: '' }}
                formControlProps={{ minWidth: '130px', width: '130px', mb: 0 }}
                onChangeCallback={fieldValue => generateCronExpression('accrue_year_month_four', fieldValue)}
              />
              <Box as="span" visibility="hidden">
                and
              </Box>
            </Flex>
          </>
        ) : null}
      </Box>

      <Flex alignItems="center" mb="6">
        <BsCalculatorFill />
        <Text ml="1">
          This totals{' '}
          <Text as="strong">
            {values.totalAccrualDays} {values.in_hours === 'true' ? 'hours' : 'days'}{' '}
          </Text>{' '}
          per year.
        </Text>
      </Flex>
    </div>
  );
};

export default CronExpressionGenerator;
